import axios from 'axios';
import Wrapper from '../../../components/Wrapper';
import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Office } from '../../../models/office';
import Title from '../../../components/Title';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import { Box, CardActions, Grid, Button, Divider, Typography, Checkbox, CardContent, Alert, FormControl, InputLabel, FormGroup, FormControlLabel } from '@mui/material';
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Collect } from "../../../models/collect";
import MenuItem from "@mui/material/MenuItem";
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { useDrawer } from '../../../components/context/DrawerContext';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EmployeCollect: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  const { drawerOpen } = useDrawer();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const [SelectedCollect, setSelectedCollect] = useState<number | null>(null);
  const [unSelectedCollect, setUnSelectedCollect] = useState<Collect[]>([]);
  const [office_id, setOffice_id] = useState('');
  const [offices, setOffices] = useState<Office[]>([]);
  const [employeeOfficeIds, setEmployeeOfficeIds] = useState<number[]>([]);
  const [collects, setCollects] = useState<Collect[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Oficinas permitidas según permisos del usuario logueado.
        const { data: permittedOfficesData } = await axios.get('offices');
        setOffices(Array.isArray(permittedOfficesData?.data) ? permittedOfficesData.data : []);

        // Oficinas actualmente asignadas al empleado (para precargar selección).
        const { data: employeeOfficeData } = await axios.get(`useroffice/${id}`);
        const ids =
          Array.isArray(employeeOfficeData?.offices)
            ? employeeOfficeData.offices.map((o: Office) => Number(o.id)).filter((n: number) => Number.isFinite(n))
            : [];
        setEmployeeOfficeIds(ids);
      } catch (error) {
        console.error('Error fetching employee/offices data: ', error);
      }
    };

    fetchData();
  }, []);

  const GetUserCollect = async (office_id: string) => {
    try {
      const numericOfficeId = Number(office_id);
      if (!Number.isFinite(numericOfficeId)) {
        setCollects([]);
        setSelectedCollect(null);
        return;
      }
      showLoading();
      let user_id = id
      const { data } = await axios.get(`usercollect/${office_id}/${user_id}`);
      setCollects(data.collect)
      setSelectedCollect(data.selectedCollect.map((collect: Collect) => collect.id));
      if (data.collect.length <= 0) {
        await new Promise((data) => setTimeout(data, 1000));
        showAlert('No se encuentran registros asociados!', 'warning');
      }
    } catch (error) {
      console.error('Error fetching user offices: ', error);
    } finally {
      hideLoading();
    }
  };

  const handleGetUserCollect = (event: SelectChangeEvent<string>) => {
    const selectedOfficeId = event.target.value as string;
    setOffice_id(selectedOfficeId);
    GetUserCollect(selectedOfficeId);
  };

  // Precarga la primera oficina (si existe) para que el modal muestre selects incluso
  // cuando el empleado aún no tiene oficinas asignadas.
  useEffect(() => {
    if (!offices || offices.length === 0) return;
    const defaultOfficeId =
      (employeeOfficeIds && employeeOfficeIds.length > 0 ? employeeOfficeIds[0] : Number(offices[0]?.id)) || null;
    if (defaultOfficeId && String(defaultOfficeId) !== office_id) {
      const next = String(defaultOfficeId);
      setOffice_id(next);
      GetUserCollect(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offices, employeeOfficeIds]);

  const handleCheckboxChange = (collectId: number) => {
    if (SelectedCollect === collectId) {
      setSelectedCollect(null);
    } else {
      setSelectedCollect(collectId);
    }
  };

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const formData = {
      collectId: SelectedCollect ? [SelectedCollect] : [],
      userId: id,
      officeId: office_id
    };
    try {
      showLoading();
      const response = await axios.post('/usercollect', formData);
      await new Promise((response) => setTimeout(response, 1000));
      showAlert('Se han registrado la información exitosamente!', 'success');
      navigate(`/administrar/empleados/${id}/cobros`);
    } catch (error) {
      console.error('Error fetching user usercollect: ', error);
    } finally {
      hideLoading();
    }
  };

  const columns: SimpleColumn[] = [
    {
      field: 'name',
      headerName: 'Nombre',
      minWidth: 240,
      wrapText: true,
    },
    {
      field: 'options',
      headerName: 'Seleccionar',
      align: 'center',
      minWidth: 200,
      renderCell: ({ row }) => (
        <Checkbox
          checked={SelectedCollect === Number(row.id)}
          onChange={() => handleCheckboxChange(Number(row.id))}
          disabled={SelectedCollect !== null && SelectedCollect !== Number(row.id)}
        />
      ),
    },
  ];

  const rows = collects.map((collect: Collect) => ({
    ...collect,
    name: collect.name,
  }));

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Asignar Empleados a Cobros</Title>
      </Box>
      <Grid item xs={12}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          Seleccionar Oficina
        </Typography>
      </Grid>
      <CardContent>
         <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth>
              <InputLabel id="form-layouts-separator-select-label">
                Seleccionar Oficina
              </InputLabel>
              <Select
                label="Seleccionar Oficina"
                value={office_id}
                id="form-layouts-separator-select"
                labelId="form-layouts-separator-select-label"
                onChange={handleGetUserCollect}
                required
                disabled={offices.length === 0}
              >
                {offices.length === 0 ? (
                  <MenuItem value="" disabled>
                    No hay oficinas disponibles
                  </MenuItem>
                ) : (
                  offices.map((office: Office) => (
                    <MenuItem key={office.id} value={String(office.id)}>
                      {office.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ marginBottom: 0 }} />
          </Grid>
        </Grid>
      </CardContent>
      <CardContent>
        <Grid container spacing={1} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <div style={{ height: '100%', width: '100%', alignItems: 'center' }}>
              {collects && collects.length > 0 ? (
                <SimpleCustomTable
                  rows={rows}
                  columns={columns}
                  maxHeight={400}
                  showBorders
                  wrapCellContent
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No hay cobros disponibles para la oficina seleccionada.
                </Typography>
              )}
            </div>
          </Grid>
        </Grid>
      </CardContent>
      <Divider sx={{ margin: 0 }} />
      <CardActions>
        <Button size='small' type='submit' sx={{ mr: 2 }} variant='contained' onClick={submit}>
          Guardar
        </Button>
        <Link to='/administrar/empleados'>
          <Button size='small' color='secondary' variant='outlined'>
            Cancelar
          </Button>
        </Link>
      </CardActions>
    </HeaderResponsive>
  );
};

export default EmployeCollect;
